import { FunctionComponent, useCallback, useMemo } from 'react';
import { StepContainer } from '../../../StepContainer';
import { BookingCheckoutContext } from '../../../CheckoutContext/BookingContext';
import { UseCourseOccurrencesOptions, useOccurrences } from '../../../data/useOccurrences';
import { OccurrenceList } from '../../../../OccurrenceList';
import Spinner from '../../../../Spinner';
import styles from './styles.module.scss';
import { BookingApplicationCheckoutContext } from '../../../CheckoutContext/BookingApplicationContext';
import { isPubliclyVisibleOccurrence } from '../../../../../services/occurrences/isPubliclyVisibleOccurrence';
import { PublicOccurrence } from '@cademy-package/sdk-component-types';

const usePublicOccurrences = (
    courseId: string,
    educatorId: string,
    options?: UseCourseOccurrencesOptions
) => {
    const { data = [], ...rest } = useOccurrences(courseId, educatorId, options);

    const occurrences = useMemo(() => {
        return data.filter(isPubliclyVisibleOccurrence);
    }, [data]);

    return {
        occurrences,
        ...rest,
    };
};

type DateStepProps = BookingCheckoutContext | BookingApplicationCheckoutContext;

export const DateStep: FunctionComponent<DateStepProps> = ({
    course,
    educator,
    submitResponse,
    checkoutState,
}) => {
    const { isLoading, occurrences } = usePublicOccurrences(course._id, course.educator_ref);
    const hidePricing = useMemo(
        () => checkoutState.origin !== 'marketplace' && educator.feature_flags?.hide_pricing,
        [checkoutState.origin, educator.feature_flags]
    );

    const onOccurrenceSelected = useCallback(
        (occurrence: PublicOccurrence) => {
            submitResponse({
                actionType: 'occurrence picked',
                payload: {
                    occurrence_id: occurrence._id,
                },
            });
        },
        [submitResponse]
    );

    const onGoBackClick = useCallback(() => {
        return history.back();
    }, []);

    if (isLoading || occurrences.length === 0) {
        return <Spinner innerClassName={styles.spinner} width={25} height={25} />;
    }

    return (
        <StepContainer
            course={course}
            optimisticCheckoutState={checkoutState}
            onBackClick={onGoBackClick}
        >
            <section className={styles.dateStepSection} aria-labelledby="date-step-header">
                <h1 className={styles.dateStepSectionHeader} id="date-step-header">
                    Select Date
                </h1>
                <OccurrenceList
                    course={course}
                    educator={educator}
                    occurrences={occurrences}
                    onOccurrenceSelected={onOccurrenceSelected}
                    occurrencesPerPage={10}
                    hidePricing={hidePricing}
                />
            </section>
        </StepContainer>
    );
};

export default DateStep;
